import React, { useEffect, useMemo, useState } from 'react';
import { FilterDrawer } from '../ui/FilterDrawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import {
  countActiveOrderFilters,
  DEFAULT_ORDER_LIST_FILTERS,
  getOrderWorkflowFilterOptions,
} from './orderListFilterUtils';

function buildSelectOptions(placeholderLabel, values, currentValue) {
  const seen = new Set();
  const sorted = (values || [])
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter((v) => v)
    .filter((v) => {
      if (seen.has(v)) return false;
      seen.add(v);
      return true;
    })
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const opts = [{ value: '', label: placeholderLabel }, ...sorted.map((v) => ({ value: v, label: v }))];
  if (currentValue && !opts.some((o) => o.value === currentValue)) {
    opts.push({ value: currentValue, label: currentValue });
  }
  return opts;
}

function collectConcepts(orders) {
  const out = [];
  for (const o of orders || []) {
    if (o.designConceptName) out.push(o.designConceptName);
  }
  return out;
}

function collectCategories(orders) {
  const out = [];
  for (const o of orders || []) {
    if (o.designCategoryName) out.push(o.designCategoryName);
    if (o.designSubCategoryName) out.push(o.designSubCategoryName);
  }
  return out;
}

/**
 * Order filters: layout aligned with the reference screen — a single light panel
 * with three rows, only visible from the “Filters” button on the list.
 */
export function OrderListFiltersModal({ open, onClose, value, onApply, orders = [] }) {
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_ORDER_LIST_FILTERS }));

  useEffect(() => {
    if (!open) return;
    setDraft({ ...DEFAULT_ORDER_LIST_FILTERS, ...value });
  }, [open, value]);

  const draftCount = useMemo(() => countActiveOrderFilters(draft), [draft]);

  const orderStatusOptions = useMemo(
    () => getOrderWorkflowFilterOptions().map((o) => ({ value: o.value, label: o.label })),
    []
  );

  const conceptOptions = useMemo(
    () => buildSelectOptions('Concept', collectConcepts(orders), draft.concept),
    [orders, draft.concept]
  );

  const categoryOptions = useMemo(
    () => buildSelectOptions('Category', collectCategories(orders), draft.category),
    [orders, draft.category]
  );

  const handleField = (key) => (e) => {
    setDraft((d) => ({ ...d, [key]: e.target.value }));
  };

  const clear = () => {
    setDraft({ ...DEFAULT_ORDER_LIST_FILTERS });
  };

  const apply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <FilterDrawer
      open={open}
      onClose={onClose}
      width="6xl"
      titleId="order-filters-title"
      title="Filters"
      description={
        draftCount === 0
          ? 'Set criteria below, then use Filter to update the list.'
          : `${draftCount} ${draftCount === 1 ? 'criterion' : 'criteria'} selected — use Filter to apply.`
      }
      headerActions={
        <button
          type="button"
          onClick={clear}
          className="rounded-lg py-1.5 px-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
        >
          Reset all
        </button>
      }
    >
      <div
        className="rounded-lg border border-slate-200/90 bg-slate-100/90 p-3 sm:p-4 md:p-5 [&_label]:text-xs [&_label]:text-slate-800"
      >
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
        >
          <Input
            type="search"
            label="Search"
            placeholder="Search by Unit, Taka No, Design No, Remark, Broker"
            value={draft.generalSearch}
            onChange={handleField('generalSearch')}
            className="rounded-md py-2.5"
          />
          <Input
            type="text"
            label="Order Id"
            placeholder="Search by Order Id"
            value={draft.orderId}
            onChange={handleField('orderId')}
            className="rounded-md py-2.5"
          />
        </div>

        <div
          className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
        >
          <Input
            type="text"
            label="Party / Butta / Mill / Border / Handwork"
            placeholder="Party / Butta / Mill / Border / Handwork"
            value={draft.party}
            onChange={handleField('party')}
            className="rounded-md py-2.5"
          />
          <Select
            label="Concept"
            value={draft.concept}
            onChange={handleField('concept')}
            options={conceptOptions}
            className="rounded-md py-2.5"
          />
          <Select
            label="Category"
            value={draft.category}
            onChange={handleField('category')}
            options={categoryOptions}
            className="rounded-md py-2.5"
          />
          <Input
            type="text"
            label="Beam Color"
            placeholder="Search Beam Color"
            value={draft.beamColor}
            onChange={handleField('beamColor')}
            className="rounded-md py-2.5"
          />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:items-end md:grid-cols-12">
          <div className="md:col-span-6">
            <Select
              label="Order Status"
              value={draft.orderStatus}
              onChange={handleField('orderStatus')}
              options={orderStatusOptions}
              className="rounded-md py-2.5"
            />
          </div>
          <div className="flex items-end justify-start md:col-span-6 md:justify-end">
            <Button
              type="button"
              onClick={apply}
              className="h-11 w-full min-w-[8.5rem] rounded-md px-8 text-base font-semibold shadow-sm md:w-auto"
            >
              Filter
            </Button>
          </div>
        </div>
      </div>
    </FilterDrawer>
  );
}

export default OrderListFiltersModal;
